/**
 * Google Docs–style header / footer for the DOCX page editor.
 *
 * Each page's top and bottom margin zones become full rich-text editing
 * areas.  Clicking a zone activates editing: the document body dims,
 * a dashed separator appears, and a label bar offers options (different
 * first page, page-number insertion, close).
 *
 * Internally two lightweight TipTap editors (one header, one footer)
 * hold the shared content.  Their HTML output is rendered statically
 * on inactive pages.
 */

import {
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontSize } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { cn } from "../../lib/cn";

// ── Types ────────────────────────────────────────────────────────

export interface HeaderFooterContent {
  headerHtml: string;
  footerHtml: string;
  firstPageHeaderHtml: string;
  firstPageFooterHtml: string;
  differentFirstPage: boolean;
}

export const DEFAULT_HF: HeaderFooterContent = {
  headerHtml: "",
  footerHtml: "",
  firstPageHeaderHtml: "",
  firstPageFooterHtml: "",
  differentFirstPage: false,
};

export type HFEditingZone = {
  position: "header" | "footer";
  page: number;
} | null;

// ── Token replacement (works on HTML) ────────────────────────────

function replaceTokens(html: string, page: number, pages: number): string {
  return html
    .replace(/\{page\}/gi, String(page))
    .replace(/\{pages\}/gi, String(pages));
}

// ── Mini TipTap editor for active zone ───────────────────────────

function HFEditor({
  initialHtml,
  onUpdate,
  placeholder,
  autoFocus,
}: {
  initialHtml: string;
  onUpdate: (html: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      Underline,
      TextStyle,
      FontSize,
      TextAlign.configure({ types: ["paragraph"] }),
      Placeholder.configure({ placeholder }),
    ],
    content: initialHtml || "",
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor: ed }) => {
      onUpdateRef.current(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: "docx-hf-editor-content",
        spellcheck: "true",
      },
    },
  });

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  return <EditorContent editor={editor} />;
}

// ── Options bar shown above/below the active zone ────────────────

function HFOptionsBar({
  position,
  differentFirstPage,
  isFirstPage,
  onToggleDifferentFirstPage,
  onInsertPageNumber,
  onClose,
}: {
  position: "header" | "footer";
  differentFirstPage: boolean;
  isFirstPage: boolean;
  onToggleDifferentFirstPage: () => void;
  onInsertPageNumber: () => void;
  onClose: () => void;
}) {
  const label =
    position === "header"
      ? isFirstPage && differentFirstPage
        ? "First Page Header"
        : "Header"
      : isFirstPage && differentFirstPage
        ? "First Page Footer"
        : "Footer";

  return (
    <div
      className={cn(
        "docx-hf-options-bar",
        position === "header"
          ? "docx-hf-options-bottom"
          : "docx-hf-options-top",
      )}
    >
      <span className="docx-hf-options-label">{label}</span>

      <div className="docx-hf-options-actions">
        <label className="docx-hf-options-checkbox">
          <input
            type="checkbox"
            checked={differentFirstPage}
            onChange={onToggleDifferentFirstPage}
          />
          <span>Different first page</span>
        </label>

        <button
          className="docx-hf-options-btn"
          onClick={onInsertPageNumber}
          title="Insert page number"
        >
          # Page number
        </button>

        <button
          className="docx-hf-options-btn docx-hf-options-close"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── Static rendered content (non-editing pages) ──────────────────

function StaticHFContent({
  html,
  page,
  pages,
}: {
  html: string;
  page: number;
  pages: number;
}) {
  const rendered = useMemo(
    () => replaceTokens(html, page, pages),
    [html, page, pages],
  );

  if (!rendered || rendered === "<p></p>") return null;

  return (
    <div
      className="docx-hf-static"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
}

// ── Single page overlay ──────────────────────────────────────────

interface PageOverlayProps {
  pageIndex: number;
  pageCount: number;
  pageHeight: number;
  pageGap: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  content: HeaderFooterContent;
  editingZone: HFEditingZone;
  onStartEdit: (pos: "header" | "footer", page: number) => void;
  onStopEdit: () => void;
  onContentChange: (c: HeaderFooterContent) => void;
  onToggleDifferentFirstPage: () => void;
}

function PageOverlay({
  pageIndex,
  pageCount,
  pageHeight,
  pageGap,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  content,
  editingZone,
  onStartEdit,
  onStopEdit,
  onContentChange,
  onToggleDifferentFirstPage,
}: PageOverlayProps) {
  const top = pageIndex * (pageHeight + pageGap);
  const isFirst = pageIndex === 0;
  const usesFirst = isFirst && content.differentFirstPage;

  const headerHtml = usesFirst ? content.firstPageHeaderHtml : content.headerHtml;
  const footerHtml = usesFirst ? content.firstPageFooterHtml : content.footerHtml;

  const editingHeader =
    editingZone?.position === "header" && editingZone.page === pageIndex;
  const editingFooter =
    editingZone?.position === "footer" && editingZone.page === pageIndex;

  const handleHeaderUpdate = useCallback(
    (html: string) => {
      const key = usesFirst ? "firstPageHeaderHtml" : "headerHtml";
      onContentChange({ ...content, [key]: html });
    },
    [content, onContentChange, usesFirst],
  );

  const handleFooterUpdate = useCallback(
    (html: string) => {
      const key = usesFirst ? "firstPageFooterHtml" : "footerHtml";
      onContentChange({ ...content, [key]: html });
    },
    [content, onContentChange, usesFirst],
  );

  const handleInsertPageNumber = useCallback(
    () => {
      const zone = editingZone?.position;
      if (!zone) return;
      const key =
        zone === "header"
          ? usesFirst
            ? "firstPageHeaderHtml"
            : "headerHtml"
          : usesFirst
            ? "firstPageFooterHtml"
            : "footerHtml";
      const current = content[key];
      const withPageNum =
        current && current !== "<p></p>"
          ? current.replace(/<\/p>$/, " {page}<\/p>")
          : "<p>{page}</p>";
      onContentChange({ ...content, [key]: withPageNum });
    },
    [content, onContentChange, editingZone, usesFirst],
  );

  const hasHeaderContent = headerHtml && headerHtml !== "<p></p>";
  const hasFooterContent = footerHtml && footerHtml !== "<p></p>";

  return (
    <div
      className="docx-hf-overlay"
      style={{
        top,
        height: pageHeight,
        paddingLeft: marginLeft,
        paddingRight: marginRight,
      }}
    >
      {/* ── Header zone ── */}
      <div
        className={cn(
          "docx-hf-zone docx-hf-header-zone",
          editingHeader && "docx-hf-active",
          !editingHeader && !hasHeaderContent && "docx-hf-empty-zone",
        )}
        style={{ height: marginTop }}
        onClick={() => {
          if (!editingHeader) onStartEdit("header", pageIndex);
        }}
      >
        {editingHeader ? (
          <>
            <div className="docx-hf-editor-wrap">
              <HFEditor
                key={`h-${pageIndex}-${usesFirst ? "first" : "std"}`}
                initialHtml={headerHtml}
                onUpdate={handleHeaderUpdate}
                placeholder="Type header text…"
                autoFocus
              />
            </div>
            <HFOptionsBar
              position="header"
              differentFirstPage={content.differentFirstPage}
              isFirstPage={isFirst}
              onToggleDifferentFirstPage={onToggleDifferentFirstPage}
              onInsertPageNumber={handleInsertPageNumber}
              onClose={onStopEdit}
            />
          </>
        ) : (
          <StaticHFContent
            html={headerHtml}
            page={pageIndex + 1}
            pages={pageCount}
          />
        )}
      </div>

      {/* ── Footer zone ── */}
      <div
        className={cn(
          "docx-hf-zone docx-hf-footer-zone",
          editingFooter && "docx-hf-active",
          !editingFooter && !hasFooterContent && "docx-hf-empty-zone",
        )}
        style={{ height: marginBottom }}
        onClick={() => {
          if (!editingFooter) onStartEdit("footer", pageIndex);
        }}
      >
        {editingFooter ? (
          <>
            <HFOptionsBar
              position="footer"
              differentFirstPage={content.differentFirstPage}
              isFirstPage={isFirst}
              onToggleDifferentFirstPage={onToggleDifferentFirstPage}
              onInsertPageNumber={handleInsertPageNumber}
              onClose={onStopEdit}
            />
            <div className="docx-hf-editor-wrap">
              <HFEditor
                key={`f-${pageIndex}-${usesFirst ? "first" : "std"}`}
                initialHtml={footerHtml}
                onUpdate={handleFooterUpdate}
                placeholder="Type footer text…"
                autoFocus
              />
            </div>
          </>
        ) : (
          <StaticHFContent
            html={footerHtml}
            page={pageIndex + 1}
            pages={pageCount}
          />
        )}
      </div>
    </div>
  );
}

// ── Main exported component ──────────────────────────────────────

interface DocxHeaderFooterProps {
  pageCount: number;
  pageHeight: number;
  pageGap: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  content: HeaderFooterContent;
  onContentChange: (content: HeaderFooterContent) => void;
  editingZone: HFEditingZone;
  onEditingZoneChange: (zone: HFEditingZone) => void;
}

export function DocxHeaderFooter({
  pageCount,
  pageHeight,
  pageGap,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  content,
  onContentChange,
  editingZone,
  onEditingZoneChange,
}: DocxHeaderFooterProps) {
  const startEdit = useCallback(
    (pos: "header" | "footer", page: number) => {
      onEditingZoneChange({ position: pos, page });
    },
    [onEditingZoneChange],
  );

  const stopEdit = useCallback(() => {
    onEditingZoneChange(null);
  }, [onEditingZoneChange]);

  const toggleDifferentFirstPage = useCallback(() => {
    onContentChange({
      ...content,
      differentFirstPage: !content.differentFirstPage,
    });
  }, [content, onContentChange]);

  return (
    <>
      {Array.from({ length: pageCount }, (_, i) => (
        <PageOverlay
          key={i}
          pageIndex={i}
          pageCount={pageCount}
          pageHeight={pageHeight}
          pageGap={pageGap}
          marginTop={marginTop}
          marginBottom={marginBottom}
          marginLeft={marginLeft}
          marginRight={marginRight}
          content={content}
          editingZone={editingZone}
          onStartEdit={startEdit}
          onStopEdit={stopEdit}
          onContentChange={onContentChange}
          onToggleDifferentFirstPage={toggleDifferentFirstPage}
        />
      ))}
    </>
  );
}
